import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query'
import { QueryKey } from 'src/reactQuery/enums/queryKey.enum'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { InboxPreviewConversationsResponse } from 'src/services/__generated__/api'

export type SentimentTab = 'all' | 'positive' | 'negative' | 'other'

interface UseGetInboxPreviewConversationsParams {
  // Converted / Committed / Unconverted (pages with conversion data only).
  conversion?: 'converted' | 'committed' | 'unconverted'
  fromDate?: string
  limit?: number
  operators?: string[]
  pageId: string
  platforms?: string[]
  // Free-text search over participant names; the backend applies it across the
  // whole collection, before pagination.
  search?: string
  sentiment?: SentimentTab
  sort?: 'newest' | 'oldest'
  // Organic / Paid (pages with ad-label attribution only).
  source?: 'organic' | 'paid'
  toDate?: string
  topics?: string[]
}

const joinCsv = (values?: string[]) =>
  values && values.length ? values.join(',') : undefined

export const useGetInboxPreviewConversationsQuery = (
  params: UseGetInboxPreviewConversationsParams
) => {
  const limit = params.limit ?? 20

  return useInfiniteQuery<InboxPreviewConversationsResponse | null>({
    enabled: !!params.pageId,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
    initialPageParam: undefined,
    // Keep the current conversation list visible while a filter change refetches
    // so toggling a topic doesn't blank the list into skeletons every time.
    placeholderData: keepPreviousData,
    queryFn: async ({ pageParam }) => {
      const { data } =
        await AnalyticsApi.v1Private.inboxAnalysisInboxPreviewConversationsList(
          {
            conversion: params.conversion,
            cursor: pageParam as string | undefined,
            fromDate: params.fromDate,
            limit,
            operators: joinCsv(params.operators),
            pageId: params.pageId,
            platforms: joinCsv(params.platforms),
            search: params.search || undefined,
            sentiment: params.sentiment ?? 'all',
            sort: params.sort ?? 'newest',
            source: params.source,
            toDate: params.toDate,
            topics: joinCsv(params.topics),
          },
          { format: 'json' }
        )
      return data as InboxPreviewConversationsResponse
    },
    queryKey: [
      QueryKey.GET_INBOX_PREVIEW_CONVERSATIONS,
      params.pageId,
      params.fromDate,
      params.toDate,
      params.sentiment ?? 'all',
      params.sort ?? 'newest',
      limit,
      joinCsv(params.platforms),
      joinCsv(params.topics),
      joinCsv(params.operators),
      params.search || '',
      params.source ?? 'all',
      params.conversion ?? 'all',
    ],
  })
}
